import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import callApi from '../../Api/config';
import { DOCUMITRA_ORDER_APPLICATION } from '../../constants/EntityName';
import { AppRoutes } from '../../constants/AppConstant';
import { getAdminName } from '../../service/CookieManager';
import { toastError } from '../../service/Toast';
import AbstractTabs from '../Entity/AbstractTabs';
import KanbanBoard, { KanbanColumn, KanbanOrder } from './KanbanBoard';
import './style.scss';

type DashboardView = 'all' | 'pending-with-us' | 'pending-with-customer' | 'government-status';
type StatusDefinition = Omit<
  KanbanColumn,
  'orders' | 'totalOrderCount' | 'loadedPage' | 'loadedOrderCount'
>;

const status = (key: string, label: string, filterValue = label): StatusDefinition => ({
  key,
  label,
  filterValue,
  count: 0,
});

const PENDING_WITH_US_STATUSES: StatusDefinition[] = [
  status('document-under-review', 'Documents Under Review', 'Document Under Review'),
  status('create-account', 'Create Accounts', 'Create Accounts'),
  status('fill-form', 'Fill Form', 'Fill Form'),
  status('form-reviewed', 'Form Reviewed by the user', 'Form Reviewed by the user'),
  status('submit-form', 'Submit Form', 'Submit Form'),
  status('send-payment-details', 'Send Payment details', 'Send Payment details'),
  status('verify-application', 'Verify Application', 'Verify Application'),
  status('vfs-payment', 'VFS Payment', 'VFS Payment'),
  status('document-printing', 'Document Printing', 'Document Printing'),
  status('final-email-to-be-sent', 'Final Email to be sent'),
  status('send-vfs-steps', 'Send VFS steps'),
];

const PENDING_WITH_CUSTOMER_STATUSES: StatusDefinition[] = [
  status('document-pending', 'Document Pending'),
  status(
    'additional-information-required',
    'Additional Information Required',
    'Additional Information Required'
  ),
  status('form-review-pending-by-applicant', 'Form Review Pending by applicant'),
  status('government-fee-payment-pending', 'Government Fee payment pending'),
  status('notary-pending-by-user', 'Notary Pending by user'),
];

const GOVERNMENT_STATUSES: StatusDefinition[] = [
  status('acknowledgement-pending', 'Acknowledgement Pending'),
  status('biometrics-appointment', 'Biometrics Appointment'),
  status('application-on-hold', 'Application on Hold'),
  status('government-approval-pending', 'Government Approval Pending'),
  status('approved-by-government', 'Approved by Government'),
  status('documitra-review-pending', 'Documitra Review Pending'),
];

const OTHER_STATUSES: StatusDefinition[] = [
  status('order-pending', 'Order Pending'),
  status('appointment-pending', 'Appointment pending'),
  status('application-completed', 'Application Completed'),
];

const ALL_STATUSES: StatusDefinition[] = [
  OTHER_STATUSES[0], // Order Pending
  PENDING_WITH_CUSTOMER_STATUSES[0],
  PENDING_WITH_US_STATUSES[0],
  PENDING_WITH_CUSTOMER_STATUSES[1],
  PENDING_WITH_US_STATUSES[1],
  PENDING_WITH_US_STATUSES[2],
  PENDING_WITH_CUSTOMER_STATUSES[2],
  PENDING_WITH_US_STATUSES[3],
  PENDING_WITH_US_STATUSES[4],
  PENDING_WITH_US_STATUSES[5],
  PENDING_WITH_CUSTOMER_STATUSES[3],
  PENDING_WITH_US_STATUSES[6],
  PENDING_WITH_US_STATUSES[7],
  PENDING_WITH_US_STATUSES[8],
  PENDING_WITH_US_STATUSES[9],
  PENDING_WITH_CUSTOMER_STATUSES[4],
  PENDING_WITH_US_STATUSES[10],
  OTHER_STATUSES[1], // Appointment pending (above Application Completed)
  OTHER_STATUSES[2], // Application Completed
  ...GOVERNMENT_STATUSES, // includes Application Closed at last
];

const STATUS_GROUPS: Record<DashboardView, StatusDefinition[]> = {
  all: ALL_STATUSES,
  'pending-with-us': PENDING_WITH_US_STATUSES,
  'pending-with-customer': PENDING_WITH_CUSTOMER_STATUSES,
  'government-status': GOVERNMENT_STATUSES,
};

const getMemberNames = (order: any, status: string): Array<{ id: string; name: string }> => {
  const normalizedStatus = status.trim().toLowerCase();
  const members = (order?.members || []).filter(
    (item: any) =>
      String(item?.status || '')
        .trim()
        .toLowerCase() === normalizedStatus
  );

  return members.map((member: any, index: number) => ({
    id: String(member?.id || `${order?.id || order?.orderId}-member-${index}`),
    name: member?.name || [member?.firstName, member?.lastName].filter(Boolean).join(' ') || '--',
  }));
};

const getOrderId = (order: any): string => String(order?.orderId || order?.id || '--');
const ORDERS_PER_PAGE = 20;

const mapOrdersToKanban = (orders: any[], status: string, keyPrefix: string): KanbanOrder[] =>
  orders.reduce<KanbanOrder[]>((mappedOrders, order, orderIndex) => {
    const orderId = getOrderId(order);
    getMemberNames(order, status).forEach((member, memberIndex) => {
      mappedOrders.push({
        id: `${keyPrefix}-${order?.id || orderId}-${member.id}-${orderIndex}-${memberIndex}`,
        orderId,
        name: member.name,
        status,
      });
    });
    return mappedOrders;
  }, []);

const Dashboard: React.FC = () => {
  const history = useHistory();
  const [selectedView, setSelectedView] = useState<DashboardView>('all');
  const activeStatuses = useMemo(() => STATUS_GROUPS[selectedView], [selectedView]);
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[]>(
    activeStatuses.map(status => ({
      ...status,
      totalOrderCount: 0,
      orders: [],
      loadedPage: 0,
      loadedOrderCount: 0,
    }))
  );
  const [loading, setLoading] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState<Record<string, boolean>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    const loadDashboardData = async () => {
      setLoading(true);
      setLoadingColumns({});
      try {
        const responses = await Promise.all([
          ...activeStatuses.map(status =>
            callApi(DOCUMITRA_ORDER_APPLICATION, 'GET', null, {
              page: 1,
              limit: ORDERS_PER_PAGE,
              memberStatus: status.filterValue,
            })
          ),
        ]);

        if (mounted) {
          setKanbanColumns(
            activeStatuses.map((status, index) => {
              const response = responses[index];
              const orders = response?.data?.data?.results || [];
              const mappedOrders = mapOrdersToKanban(orders, status.filterValue, status.key);
              return {
                ...status,
                count: Math.max(Number(response?.data?.data?.totalCount ?? 0), mappedOrders.length),
                totalOrderCount: Number(response?.data?.data?.totalCount ?? orders.length),
                orders: mappedOrders,
                loadedPage: 1,
                loadedOrderCount: orders.length,
              };
            })
          );
        }
      } catch {
        if (mounted) toastError('Failed to load dashboard data');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDashboardData();
    return () => {
      mounted = false;
    };
  }, [activeStatuses, refreshKey]);

  const adminName = getAdminName() || 'there';
  const handleStatusClick = (status: KanbanColumn) => {
    if (status.filterValue) {
      history.push(
        `${AppRoutes.DocumitraOrderApplication}?memberStatus=${encodeURIComponent(
          status.filterValue
        )}`
      );
    }
  };
  const handleOrderClick = (order: KanbanOrder) => {
    history.push(
      `${AppRoutes.DocumitraOrderApplication}?search=${encodeURIComponent(
        order.orderId
      )}&memberStatus=${encodeURIComponent(order.status)}`
    );
  };
  const handleLoadMore = async (column: KanbanColumn) => {
    if (loadingColumns[column.key] || column.loadedOrderCount >= column.totalOrderCount) return;

    setLoadingColumns(previous => ({ ...previous, [column.key]: true }));
    try {
      const response = await callApi(DOCUMITRA_ORDER_APPLICATION, 'GET', null, {
        page: column.loadedPage + 1,
        limit: ORDERS_PER_PAGE,
        memberStatus: column.filterValue,
      });
      const newOrders = response?.data?.data?.results || [];
      const newMappedOrders = mapOrdersToKanban(newOrders, column.filterValue, column.key);
      setKanbanColumns(previous =>
        previous.map(previousColumn =>
          previousColumn.key === column.key
            ? {
                ...previousColumn,
                count: Math.max(
                  previousColumn.count,
                  previousColumn.orders.length + newMappedOrders.length
                ),
                orders: [...previousColumn.orders, ...newMappedOrders],
                loadedPage: column.loadedPage + 1,
                loadedOrderCount: column.loadedOrderCount + newOrders.length,
              }
            : previousColumn
        )
      );
    } catch {
      toastError(`Failed to load more ${column.label.toLowerCase()} orders`);
    } finally {
      setLoadingColumns(previous => ({ ...previous, [column.key]: false }));
    }
  };

  return (
    <AbstractTabs
      activeKey="0"
      entityName="Dashboard"
      updateFormVisibility={() => undefined}
      onTabClick={() => undefined}
    >
      <div className="dashboard-page">
        <div className="dashboard-heading">
          <div>
            <h1>Welcome back, {adminName}</h1>
            <p>Here&apos;s what&apos;s happening across Documitra right now.</p>
          </div>
          <div className="dashboard-heading-actions">
            <div className="dashboard-view-buttons" role="group" aria-label="Order status views">
              {(
                [
                  ['all', 'All'],
                  ['pending-with-us', 'Pending with us'],
                  ['pending-with-customer', 'Pending from customer'],
                  ['government-status', 'Government status'],
                ] as Array<[DashboardView, string]>
              ).map(([view, label]) => (
                <button
                  type="button"
                  key={view}
                  className={`dashboard-view-button ${selectedView === view ? 'is-active' : ''}`}
                  onClick={() => setSelectedView(view)}
                  aria-pressed={selectedView === view}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="dashboard-refresh-button"
              onClick={() => setRefreshKey(previousKey => previousKey + 1)}
              aria-label="Refresh dashboard data"
              title="Refresh dashboard data"
            >
              <ReloadOutlined spin={loading} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {loading ? (
          <Spin />
        ) : (
          <>
            <KanbanBoard
              columns={kanbanColumns}
              onColumnClick={handleStatusClick}
              onOrderClick={handleOrderClick}
              loadingColumns={loadingColumns}
              onLoadMore={handleLoadMore}
            />
          </>
        )}
      </div>
    </AbstractTabs>
  );
};

export default Dashboard;
